'use client';
import { FC, useMemo, useState } from 'react';
import { Box, Typography, Paper, Stack, MenuItem, Select } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { createProjectActivitySubmitValidation } from './activityValidation';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import { useBackend } from '@/hooks/useBackend';
import {
  ProjectActivityDto,
  PrivilegeEnum,
  ActivityTypeDto,
  UpdateProjectActivityRequest,
  StatusDto,
} from '@/api/ApiClient';
import { formatDate } from '@/lib/utils/dateUtils';
import { Footer } from '@/components/details/Footer';
import DetailField from '@/components/common/DetailField';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { getVisibilityTypeDisplay, VISIBILITY_OPTIONS } from '@/lib/types/visibilityTypes';
import { getPrivilegeForEntity } from '@/types/tree';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useProjectActivityData } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { useSubmitWithValidation } from '@/hooks/useSubmitWithValidation';
import { getActivityTypeDisplay } from '@/lib/types/activityTypes';

import { getProblemDetail } from '@/lib/utils/apiErrors';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createProjectActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';

export const selectOptions: OptionKey[] = ['researcherOptions', 'facultyOptions', 'staff', 'users'];

const ProjectActivityDetails: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();

  const { setDataModified, updateDataPartial, updateData } = useTree();
  const { selectedNode } = useTreeSelection();
  const [isManuallyEditing, setIsManuallyEditing] = useState(false);
  const { submitWithValidation } = useSubmitWithValidation();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const activity = useProjectActivityData(selectedNode);

  // Validation state
  const [descError, setDescError] = useState(false);

  const privilege = useMemo(
    () => getPrivilegeForEntity('projectActivity', activity) ?? PrivilegeEnum.UserProject,
    [activity]
  );

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  /**
   * Resolve activity user identifiers to display names.
   * We need both key/value option maps and raw collaborator lists because
   * project activities can store login/eppn/id depending on the source path.
   */
  const getRealUserName = (userId: string) => {
    if (!userId) return 'N/A';
    const fromKeyValues =
      options.researcherOptions?.find((k) => k.key === userId)?.value ||
      options.facultyOptions?.find((k) => k.key === userId)?.value;
    if (fromKeyValues) return fromKeyValues;

    const collaborators = [...(options.staff || []), ...(options.users || [])];
    const matched = collaborators.find((u) => u.id === userId);
    if (matched) {
      const name = `${matched.firstName || ''} ${matched.lastName || ''}`.trim();
      if (name) return name;
      return matched.id || userId;
    }
    return userId;
  };

  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? date : undefined;
  };

  const handleModification = (modified: Partial<ProjectActivityDto>) => {
    if (!selectedNode) return;
    updateDataPartial(selectedNode.dataId, modified);

    // Clear validation errors when user starts typing
    if (modified.desc !== undefined) {
      setDescError(false);
    }
    if (modified.type !== undefined) {
      clearFieldError('files');
    }
  };

  // Clear field errors when user starts editing
  const clearFieldError = (fieldKey: string) => {
    if (fieldErrors[fieldKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  // Validation function
  const isValid = () => {
    if (!activity) return false;
    return validateRequiredToSave(createProjectActivitySaveValidation(activity)).isValid;
  };

  const handleSave = async () => {
    if (!activity?.id || !selectedNode) return;

    const saveValidation = validateRequiredToSave(createProjectActivitySaveValidation(activity));
    setDescError(!!saveValidation.fieldErrors.desc);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields', 'error');
      return;
    }

    try {
      const request: UpdateProjectActivityRequest = {
        id: activity.id,
        date: activity.date,
        userId: activity.userId,
        runtime: activity.runtime,
        visibility: activity.visibility,
        type: activity.type,
        desc: activity.desc,
      };
      const updatedData = await backend.projectActivity.update(activity.id, request);
      updateData(selectedNode.dataId, updatedData);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after save
      setFieldErrors({});
      showSnackbar('Project activity saved successfully', 'success');
    } catch (error) {
      log.error('[ProjectActivityDetails] Failed to save activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save project activity.', 'error');
    }
  };

  // Custom submit handler with validation
  const handleSubmit = async (): Promise<boolean> => {
    if (!activity || !selectedNode) return false;

    // Fetch files only when submit is triggered
    let fileList: any[] = [];
    if (activity.id) {
      try {
        fileList = await backend.projectActivity.getFileGroups(activity.id);
      } catch (error) {
        log.error('[ProjectActivityDetails] Failed to load files for validation:', error);
        fileList = [];
      }
    }

    const validationFields = createProjectActivitySubmitValidation({ ...activity, files: fileList || [] });
    const result = await submitWithValidation(selectedNode, validationFields, 'Project Activity');
    if (!result.success && result.fieldErrors) {
      setFieldErrors(result.fieldErrors);
      setIsManuallyEditing(true); // Switch to edit mode
    } else {
      setFieldErrors({});
    }
    return result.success;
  };

  if (!options || optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }

  if (!activity || selectedNode?.type !== 'projectActivity') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {!activity ? 'Select a node to view details' : 'Selected node is not a project activity.'}
        </Typography>
      </Box>
    );
  }

  const isSubmitted = activity.status === StatusDto.Submitted;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={3}>
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField label="Type" value={getActivityTypeDisplay(activity.type)} isReadOnly={true} />
                {/* Visibility */}
                {activity.type !== 'PUB' ? (
                  <DetailField
                    label="Visibility"
                    value={activity.visibility ? getVisibilityTypeDisplay(activity.visibility) : 'N/A'}
                    isReadOnly={isReadOnly}
                  >
                    <Select
                      value={activity.visibility ?? ''}
                      onChange={(e) => handleModification({ visibility: e.target.value })}
                      fullWidth
                      size="small"
                    >
                      {VISIBILITY_OPTIONS.map((value) => (
                        <MenuItem key={value} value={value}>
                          {getVisibilityTypeDisplay(value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </DetailField>
                ) : null}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* Created by */}
                <DetailField
                  label="Created by"
                  value={activity.userId ? getRealUserName(activity.userId) : 'N/A'}
                  isReadOnly={true}
                />

                {/* Date */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DetailField
                    label="Date"
                    value={activity.date ? formatDate(activity.date) : 'N/A'}
                    isReadOnly={isReadOnly || isSubmitted}
                  >
                    <DatePicker
                      value={activity.date ? new Date(activity.date) : null}
                      onChange={(newValue) => {
                        handleModification({
                          date: convertDateForBackend(newValue),
                        });
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                  </DetailField>
                </LocalizationProvider>
              </Box>

              {/* Description */}
              {String(activity.type) !== 'CERT' && activity.type !== ActivityTypeDto.Publication ? (
                <DetailField label="Description" value={activity.desc || ''} isReadOnly={isReadOnly || isSubmitted}>
                  <DebouncedTextField
                    value={activity.desc || ''}
                    onUpdate={(value) => handleModification({ desc: value })}
                    fullWidth
                    size="small"
                    error={descError}
                    helperText={descError ? 'Description is required' : ''}
                  />
                </DetailField>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <Footer
          lastChangedDate={activity.lastChangedDate}
          lastChangedBy={getRealUserName(activity.lastChangedByUser || '')}
          status={activity.status}
          isReadOnly={isReadOnly}
          setIsReadOnly={(val) => setIsManuallyEditing(!val)}
          onSave={handleSave}
          privilege={privilege}
          isFormValid={isValid()}
          onSubmit={handleSubmit}
        />
      </Box>
    </Box>
  );
};

export default ProjectActivityDetails;
